import { t } from "@travily/i18n";
import { ActionMenu } from "@ui/components/ActionMenu";
import { ChevronDownIcon } from "@ui/components/Icon/ChevronDownIcon";
import { OutlineButton } from "@ui/components/OutlineButton";
import { tv } from "@ui/lib/tv";
import { composeRenderProps } from "@ui/primitives/shared";
import { ToggleButton } from "@ui/primitives/ToggleButton";
import { CONVERSATION_KINDS, type ConversationKind } from "../use-conversation-filters";

// i18n-tasks-use t('components.views.messages.filters.kinds.all')
// i18n-tasks-use t('components.views.messages.filters.kinds.communities')
// i18n-tasks-use t('components.views.messages.filters.kinds.families')
// i18n-tasks-use t('components.views.messages.filters.kinds.people')

// Green, not the coral the thread uses: on this surface coral means "you", and a filter chip is not
// about you. Matches the selection accent PillTabs already uses.
const unreadToggle = tv({
  base: [
    "cursor-pointer whitespace-nowrap rounded-full border-2 px-3 py-1 font-semibold text-base outline-none",
    "transition-colors focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2",
    "border-neutral-100 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50",
    "selected:border-green-400 selected:bg-green-50 selected:text-green-700",
  ],
});

interface ConversationFiltersProps {
  kind: ConversationKind;
  onKindChange: (kind: ConversationKind) => void;
  onUnreadOnlyChange: (unreadOnly: boolean) => void;
  unreadOnly: boolean;
}

export function ConversationFilters({ kind, onKindChange, onUnreadOnlyChange, unreadOnly }: ConversationFiltersProps) {
  const kindLabel = (value: ConversationKind) => t(`components.views.messages.filters.kinds.${value}`);
  const menuLabel = t("components.views.messages.filters.kind_label");

  return (
    <div className="flex items-center gap-2">
      <ActionMenu
        items={CONVERSATION_KINDS.map((value) => ({ id: value, label: kindLabel(value) }))}
        label={menuLabel}
        onSelectionChange={(value) => onKindChange(value as ConversationKind)}
        placement="bottom start"
        selectedKey={kind}
        selectionMode="single"
        title={menuLabel}
        trigger={
          <OutlineButton
            aria-label={menuLabel}
            className={kind === "all" ? undefined : "border-green-400 bg-green-50 text-green-700"}
            size="36"
            trailIcon={<ChevronDownIcon className="size-4" />}
          >
            {kindLabel(kind)}
          </OutlineButton>
        }
      />
      <ToggleButton
        className={composeRenderProps("", (className, renderProps) => unreadToggle({ ...renderProps, className }))}
        isSelected={unreadOnly}
        onChange={onUnreadOnlyChange}
      >
        {t("components.views.messages.filters.unread")}
      </ToggleButton>
    </div>
  );
}
